/**
 * section-sw-rail.js — brand-rail behavior (H-C lane).
 *
 * Chevron paging + arrow state run on NATIVE scroll only:
 * `track.scrollBy({ left, behavior: 'smooth' })` over the CSS scroll-snap
 * baseline. No GSAP Draggable, no scroll proxy — the proxy is what killed the
 * arrows in the prototype (H-H defect D-4). Touch scrolling stays native
 * (h3 §2.4, c6 R-06). Desktop bindings are cheap and unconditional, so a
 * resize across the 1024px breakpoint never leaves a rail half-initialized
 * (D-4 responsive note); the controls are simply hidden by CSS below 1024px.
 *
 * The only GSAP piece is the one-time reveal-on-enter stagger (h3 §4,
 * c6 R-07), registered through the SWAnimations gate (assets/animations.js)
 * exactly per its contract: reduced motion never creates a timeline, the
 * IntersectionObserver defers setup, and the returned cleanup runs on
 * shopify:section:unload. The section root only carries data-needs-gsap /
 * data-needs-scrolltrigger when its intensity setting is not "none".
 */
(function () {
  'use strict';

  if (!customElements.get('sw-scroll-rail')) {
    class SwScrollRail extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-rail-track]');
        this.prevBtn = this.querySelector('[data-rail-prev]');
        this.nextBtn = this.querySelector('[data-rail-next]');
        if (!this.track) return;

        var self = this;
        this._onScroll = function () {
          if (self._raf) return;
          self._raf = requestAnimationFrame(function () {
            self._raf = null;
            self.updateArrowState();
          });
        };
        this._onResize = this._onScroll;

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', function () {
            self.page(-1);
          });
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', function () {
            self.page(1);
          });
        }
        this.track.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize);

        this.classList.add('is-ready');
        this.updateArrowState();
      }

      disconnectedCallback() {
        if (this._raf) cancelAnimationFrame(this._raf);
        if (this.track && this._onScroll) this.track.removeEventListener('scroll', this._onScroll);
        if (this._onResize) window.removeEventListener('resize', this._onResize);
      }

      page(dir) {
        var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.track.scrollBy({
          left: this.track.clientWidth * 0.9 * dir,
          behavior: reduce ? 'auto' : 'smooth'
        });
      }

      updateArrowState() {
        var t = this.track;
        if (this.prevBtn) this.prevBtn.classList.toggle('is-disabled', t.scrollLeft <= 4);
        if (this.nextBtn) {
          this.nextBtn.classList.toggle('is-disabled', t.scrollLeft >= t.scrollWidth - t.clientWidth - 4);
        }
      }
    }

    customElements.define('sw-scroll-rail', SwScrollRail);
  }

  /* Reveal-on-enter (one-time fade+rise stagger) through the SWAnimations
     gate. Guarded so repeated <script> tags (one per rail section on a page)
     register the definition once. */
  if (window.SWAnimations && !window.__swRailRevealRegistered) {
    window.__swRailRevealRegistered = true;
    window.SWAnimations.register('brand-rail', '[data-sw-rail-reveal]', function (el, gsap) {
      var track = el.querySelector('[data-rail-track]');
      if (!track || !window.ScrollTrigger) return;
      var cards = Array.prototype.slice.call(track.children);
      if (!cards.length) return;
      var subtle = el.getAttribute('data-sw-intensity') === 'subtle';
      var triggers = window.ScrollTrigger.batch(cards, {
        start: 'top 90%',
        once: true,
        onEnter: function (batch) {
          gsap.fromTo(
            batch,
            { autoAlpha: 0, y: subtle ? 12 : 24 },
            {
              autoAlpha: 1,
              y: 0,
              duration: subtle ? 0.35 : 0.5,
              stagger: subtle ? 0.04 : 0.06,
              ease: 'power2.out',
              overwrite: true
            }
          );
        }
      });
      return function () {
        triggers.forEach(function (trigger) {
          trigger.kill();
        });
      };
    });
  }
})();
